import { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import Prism from "prismjs";
import "prismjs/components/prism-javascript";
import "prismjs/components/prism-jsx";
import "prismjs/components/prism-typescript";
import "prismjs/components/prism-tsx";
import "prismjs/components/prism-python";
import "prismjs/components/prism-bash";
import "prismjs/components/prism-json";
import "prismjs/components/prism-yaml";
import "prismjs/components/prism-markup";
import "prismjs/components/prism-css";
import "prismjs/components/prism-scss";
import "prismjs/components/prism-markdown";
import "prismjs/components/prism-go";
import "prismjs/components/prism-rust";
import "prismjs/components/prism-ruby";
import "prismjs/components/prism-java";
import "prismjs/components/prism-c";
import "prismjs/components/prism-cpp";
import "prismjs/components/prism-csharp";
import "prismjs/components/prism-php";
import "prismjs/components/prism-sql";
import "prismjs/components/prism-graphql";
import "prismjs/components/prism-toml";
import "prismjs/components/prism-docker";
import "prismjs/components/prism-makefile";
import "prismjs/themes/prism-tomorrow.css";
import {
  Github, RefreshCw, Loader2, LogOut, Folder, FolderOpen, FileCode,
  ChevronRight, ChevronDown, Sparkles, ExternalLink, FileText,
  Search, Send, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { getApiUrl, getBackendUrl } from "@/utils/api";

const API = getApiUrl();
const BACKEND = getBackendUrl();

/**
 * Build a nested folder tree from GitHub's flat recursive tree response.
 * Each node: { name, path, type ('tree'|'blob'), children: Map }
 */
function buildTree(flat) {
  const root = { name: "", path: "", type: "tree", children: new Map() };
  for (const entry of flat || []) {
    const parts = (entry.path || "").split("/");
    let node = root;
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      const isLast = i === parts.length - 1;
      if (!node.children.has(part)) {
        node.children.set(part, {
          name: part,
          path: parts.slice(0, i + 1).join("/"),
          type: isLast ? entry.type : "tree",
          children: new Map(),
        });
      }
      node = node.children.get(part);
    }
  }
  return root;
}

function TreeNode({ node, depth, expanded, onToggle, onOpenFile, selectedPath, matchSet }) {
  const children = useMemo(() => {
    let arr = Array.from(node.children.values());
    if (matchSet) arr = arr.filter((c) => matchSet.has(c.path));
    arr.sort((a, b) => {
      if (a.type !== b.type) return a.type === "tree" ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
    return arr;
  }, [node.children, matchSet]);

  return (
    <ul className="text-sm">
      {children.map((child) => {
        const isFolder = child.type === "tree";
        const isOpen = expanded.has(child.path);
        const isSelected = selectedPath === child.path;
        return (
          <li key={child.path}>
            <button
              type="button"
              data-testid={`gh-tree-${isFolder ? "folder" : "file"}-${child.path}`}
              onClick={() => (isFolder ? onToggle(child.path) : onOpenFile(child.path))}
              className={`w-full flex items-center gap-1 px-2 py-1 rounded-sm hover:bg-neutral-100 text-left ${
                isSelected ? "bg-blue-50 text-blue-700" : ""
              }`}
              style={{ paddingLeft: `${depth * 12 + 8}px` }}
            >
              {isFolder ? (
                <>
                  {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  {isOpen ? <FolderOpen size={14} className="text-amber-600" /> : <Folder size={14} className="text-amber-600" />}
                </>
              ) : (
                <>
                  <span style={{ width: 14 }} />
                  <FileCode size={14} className="text-neutral-500" />
                </>
              )}
              <span className="truncate">{child.name}</span>
            </button>
            {isFolder && isOpen && child.children.size > 0 && (
              <TreeNode
                node={child}
                depth={depth + 1}
                expanded={expanded}
                onToggle={onToggle}
                onOpenFile={onOpenFile}
                selectedPath={selectedPath}
                matchSet={matchSet}
              />
            )}
          </li>
        );
      })}
    </ul>
  );
}

/** Guess the syntax highlighter language from the filename extension. */
function detectLanguage(path) {
  const ext = (path || "").split(".").pop().toLowerCase();
  const map = {
    js: "javascript", jsx: "jsx", ts: "typescript", tsx: "tsx",
    py: "python", rb: "ruby", go: "go", rs: "rust", java: "java",
    c: "c", h: "c", cpp: "cpp", hpp: "cpp", cs: "csharp",
    php: "php", swift: "swift", kt: "kotlin", scala: "scala",
    html: "markup", xml: "markup", css: "css", scss: "scss", sass: "sass",
    json: "json", yaml: "yaml", yml: "yaml", toml: "toml",
    sh: "bash", bash: "bash", zsh: "bash", ps1: "powershell",
    sql: "sql", graphql: "graphql", md: "markdown", mdx: "markdown",
    dockerfile: "docker", makefile: "makefile", env: "bash",
    vue: "markup", svelte: "markup",
  };
  return map[ext] || "text";
}

export default function GitHubExplorerTab() {
  const [status, setStatus] = useState({ connected: false });
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [repos, setRepos] = useState([]);
  const [loadingRepos, setLoadingRepos] = useState(false);
  const [selectedRepo, setSelectedRepo] = useState(null); // {owner, name, default_branch}
  const [tree, setTree] = useState(null);
  const [loadingTree, setLoadingTree] = useState(false);
  const [expanded, setExpanded] = useState(new Set());
  const [selectedPath, setSelectedPath] = useState("");
  const [fileContent, setFileContent] = useState(null); // {name, path, content, is_binary, html_url}
  const [loadingFile, setLoadingFile] = useState(false);
  const [readme, setReadme] = useState("");
  const [loadingReadme, setLoadingReadme] = useState(false);
  const [aiExplanation, setAiExplanation] = useState("");
  const [chatMessages, setChatMessages] = useState([]); // [{role, content}]
  const [aiLoading, setAiLoading] = useState(false);
  const [chatInput, setChatInput] = useState("");
  const [filter, setFilter] = useState("");
  const [fileFilter, setFileFilter] = useState("");
  const [treeTruncated, setTreeTruncated] = useState(false);
  const [flatBlobs, setFlatBlobs] = useState([]); // string[] file paths

  const loadStatus = useCallback(async () => {
    setLoadingStatus(true);
    try {
      const r = await axios.get(`${API}/github/status`, { withCredentials: true });
      setStatus(r.data || { connected: false });
    } catch (_err) {
      setStatus({ connected: false });
    } finally {
      setLoadingStatus(false);
    }
  }, []);

  const loadRepos = useCallback(async () => {
    setLoadingRepos(true);
    try {
      const r = await axios.get(`${API}/github/repos`, { withCredentials: true });
      setRepos(r.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load repositories");
    } finally {
      setLoadingRepos(false);
    }
  }, []);

  // On mount: check status; if connected, load repos.
  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => {
    if (status.connected) loadRepos();
  }, [status.connected, loadRepos]);

  // Handle callback params after OAuth bounce.
  useEffect(() => {
    const url = new URL(window.location.href);
    const connected = url.searchParams.get("gh_connected");
    const error = url.searchParams.get("gh_error");
    if (connected === "1") {
      toast.success("GitHub connected");
      url.searchParams.delete("gh_connected");
      window.history.replaceState({}, "", url.toString());
      loadStatus();
    }
    if (error) {
      toast.error(`GitHub connection failed: ${error}`);
      url.searchParams.delete("gh_error");
      window.history.replaceState({}, "", url.toString());
    }
  }, [loadStatus]);

  const handleConnect = () => {
    // Full page redirect so cookies persist across GitHub -> our callback.
    window.location.href = `${BACKEND}/api/github/connect`;
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Disconnect GitHub? This will revoke access.")) return;
    try {
      await axios.delete(`${API}/github/disconnect`, { withCredentials: true });
      toast.success("GitHub disconnected");
      setStatus({ connected: false });
      setRepos([]);
      setSelectedRepo(null);
      setTree(null);
      setFileContent(null);
      setReadme("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to disconnect");
    }
  };

  const openRepo = async (repo) => {
    setSelectedRepo(repo);
    setTree(null);
    setFlatBlobs([]);
    setExpanded(new Set());
    setSelectedPath("");
    setFileContent(null);
    setReadme("");
    setAiExplanation("");
    setChatMessages([]);
    setFileFilter("");
    setLoadingTree(true);
    setLoadingReadme(true);
    try {
      const [treeRes, readmeRes] = await Promise.all([
        axios.get(`${API}/github/repos/${repo.owner}/${repo.name}/tree`, { withCredentials: true }),
        axios.get(`${API}/github/repos/${repo.owner}/${repo.name}/readme`, { withCredentials: true }),
      ]);
      const treeEntries = treeRes.data?.tree || [];
      setTree(buildTree(treeEntries));
      setFlatBlobs(treeEntries.filter((t) => t.type === "blob").map((t) => t.path));
      setTreeTruncated(!!treeRes.data?.truncated);
      setReadme(readmeRes.data?.content || "");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load repository");
    } finally {
      setLoadingTree(false);
      setLoadingReadme(false);
    }
  };

  const toggleFolder = (path) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path); else next.add(path);
      return next;
    });
  };

  const openFile = async (path) => {
    if (!selectedRepo) return;
    setSelectedPath(path);
    setFileContent(null);
    setAiExplanation("");
    setChatMessages([]);
    setChatInput("");
    setLoadingFile(true);
    try {
      const r = await axios.get(
        `${API}/github/repos/${selectedRepo.owner}/${selectedRepo.name}/file`,
        { params: { path }, withCredentials: true },
      );
      setFileContent(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load file");
    } finally {
      setLoadingFile(false);
    }
  };

  /** One-shot summary (used by the top "AI Explain" button as a quick starter). */
  const explainFile = async () => {
    if (!selectedRepo || !fileContent) return;
    setAiLoading(true);
    setAiExplanation("");
    try {
      const r = await axios.post(
        `${API}/github/repos/${selectedRepo.owner}/${selectedRepo.name}/ai-explain`,
        {},
        { params: { path: fileContent.path }, withCredentials: true },
      );
      setAiExplanation(r.data?.explanation || "");
      if (r.data?.truncated) {
        toast.message("File was long — AI saw a truncated version.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "AI explain failed");
    } finally {
      setAiLoading(false);
    }
  };

  /** Send a chat message grounded in the current file. */
  const sendChatMessage = async (text) => {
    const trimmed = (text || "").trim();
    if (!trimmed || !selectedRepo || !fileContent || aiLoading) return;
    const nextMessages = [...chatMessages, { role: "user", content: trimmed }];
    setChatMessages(nextMessages);
    setChatInput("");
    setAiLoading(true);
    try {
      const r = await axios.post(
        `${API}/github/repos/${selectedRepo.owner}/${selectedRepo.name}/ai-chat`,
        { messages: nextMessages },
        { params: { path: fileContent.path }, withCredentials: true },
      );
      setChatMessages((prev) => [...prev, { role: "assistant", content: r.data?.reply || "" }]);
      if (r.data?.truncated && chatMessages.length === 0) {
        toast.message("File was long — AI saw a truncated version.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Chat failed");
      // Roll back the optimistic user message so they can retry
      setChatMessages((prev) => prev.slice(0, -1));
      setChatInput(trimmed);
    } finally {
      setAiLoading(false);
    }
  };

  const filteredRepos = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return repos;
    return repos.filter((r) => r.full_name.toLowerCase().includes(q));
  }, [repos, filter]);

  /**
   * When `fileFilter` is active, compute a Set of paths that should be shown
   * in the tree — every matching blob + every ancestor folder of a match.
   * Also auto-expand those ancestor folders so the user sees the matches.
   */
  const searchMatchSet = useMemo(() => {
    const q = fileFilter.trim().toLowerCase();
    if (!q) return null;
    const set = new Set();
    for (const path of flatBlobs) {
      if (path.toLowerCase().includes(q)) {
        set.add(path);
        const parts = path.split("/");
        let cur = "";
        for (let i = 0; i < parts.length - 1; i++) {
          cur = cur ? `${cur}/${parts[i]}` : parts[i];
          set.add(cur);
        }
      }
    }
    return set;
  }, [fileFilter, flatBlobs]);

  // Auto-expand ancestor folders of search matches whenever the query changes.
  useEffect(() => {
    if (!searchMatchSet) return;
    setExpanded((prev) => {
      const next = new Set(prev);
      for (const p of searchMatchSet) next.add(p);
      return next;
    });
  }, [searchMatchSet]);

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  if (loadingStatus) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground p-6">
        <Loader2 size={16} className="animate-spin" /> Checking GitHub connection…
      </div>
    );
  }

  if (!status.connected) {
    return (
      <div className="max-w-xl mx-auto p-8 border border-border bg-white text-center space-y-4" data-testid="gh-connect-panel">
        <Github size={40} className="mx-auto text-neutral-800" />
        <h3 className="text-xl font-serif">Connect your GitHub account</h3>
        <p className="text-sm text-muted-foreground">
          Browse your repositories, read files with syntax highlighting, and
          get AI-powered explanations of what each file does.
        </p>
        <Button onClick={handleConnect} data-testid="gh-connect-btn" className="gap-2">
          <Github size={16} /> Connect GitHub
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="gh-explorer-root">
      {/* Header: connected identity + disconnect */}
      <div className="flex items-center justify-between bg-white border border-border p-4">
        <div className="flex items-center gap-3">
          {status.avatar ? (
            <img src={status.avatar} alt="" className="w-8 h-8 rounded-full" />
          ) : (
            <Github size={28} />
          )}
          <div className="text-sm">
            <div className="font-medium">Connected as @{status.login}</div>
            <div className="text-xs text-muted-foreground">Scope: {status.scope || "public"}</div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={loadRepos} disabled={loadingRepos} data-testid="gh-refresh-repos">
            {loadingRepos ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            <span className="ml-1">Refresh</span>
          </Button>
          <Button variant="outline" size="sm" onClick={handleDisconnect} data-testid="gh-disconnect-btn">
            <LogOut size={14} className="mr-1" /> Disconnect
          </Button>
        </div>
      </div>

      {/* Repo picker */}
      <div className="bg-white border border-border p-4 space-y-3">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <Input
            placeholder="Filter repositories…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="max-w-sm"
            data-testid="gh-repo-filter"
          />
          <Select
            value={selectedRepo ? `${selectedRepo.owner}/${selectedRepo.name}` : ""}
            onValueChange={(val) => {
              const repo = repos.find((r) => r.full_name === val);
              if (repo) {
                openRepo({
                  owner: repo.owner, name: repo.name, default_branch: repo.default_branch,
                  description: repo.description, language: repo.language, private: repo.private,
                });
              }
            }}
          >
            <SelectTrigger className="w-full sm:w-96" data-testid="gh-repo-select">
              <SelectValue placeholder={loadingRepos ? "Loading…" : "Select a repository"} />
            </SelectTrigger>
            <SelectContent className="max-h-80">
              {filteredRepos.map((r) => (
                <SelectItem key={r.id} value={r.full_name}>
                  <span className="inline-flex items-center gap-2">
                    {r.private && <span className="text-[10px] px-1 border border-neutral-300 rounded">private</span>}
                    {r.full_name}
                  </span>
                </SelectItem>
              ))}
              {filteredRepos.length === 0 && (
                <div className="px-3 py-2 text-sm text-muted-foreground">No repositories found</div>
              )}
            </SelectContent>
          </Select>
          {selectedRepo && (
            <a
              href={`https://github.com/${selectedRepo.owner}/${selectedRepo.name}`}
              target="_blank" rel="noreferrer"
              className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1"
            >
              Open on GitHub <ExternalLink size={12} />
            </a>
          )}
        </div>
        {selectedRepo?.description && (
          <p className="text-xs text-muted-foreground">{selectedRepo.description}</p>
        )}
        {treeTruncated && (
          <p className="text-xs text-amber-700">
            Note: repository is very large — the file tree was truncated by GitHub.
          </p>
        )}
      </div>

      {/* Body: file tree + viewer */}
      {selectedRepo && (
        <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4">
          {/* Tree */}
          <div className="bg-white border border-border max-h-[70vh] overflow-hidden flex flex-col" data-testid="gh-tree-panel">
            {/* File search */}
            <div className="p-2 border-b border-border">
              <div className="relative">
                <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  value={fileFilter}
                  onChange={(e) => setFileFilter(e.target.value)}
                  placeholder="Search files by name…"
                  className="w-full pl-7 pr-7 py-1.5 text-xs border border-border rounded-sm focus:outline-none focus:ring-1 focus:ring-primary"
                  data-testid="gh-file-search"
                />
                {fileFilter && (
                  <button
                    type="button"
                    onClick={() => setFileFilter("")}
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    data-testid="gh-file-search-clear"
                    aria-label="Clear search"
                  >
                    <X size={13} />
                  </button>
                )}
              </div>
              {searchMatchSet && (
                <div className="text-[10px] text-muted-foreground mt-1 px-1">
                  {Array.from(searchMatchSet).filter((p) => flatBlobs.includes(p)).length} file(s) matched
                </div>
              )}
            </div>
            <div className="flex-1 overflow-auto p-1">
              {loadingTree ? (
                <div className="p-3 text-sm text-muted-foreground flex items-center gap-2">
                  <Loader2 size={14} className="animate-spin" /> Loading files…
                </div>
              ) : tree ? (
                <TreeNode
                  node={tree}
                  depth={0}
                  expanded={expanded}
                  onToggle={toggleFolder}
                  onOpenFile={openFile}
                  selectedPath={selectedPath}
                  matchSet={searchMatchSet}
                />
              ) : (
                <div className="p-3 text-sm text-muted-foreground">No files.</div>
              )}
            </div>
          </div>

          {/* Viewer */}
          <div className="bg-white border border-border p-4 min-h-[40vh]" data-testid="gh-viewer-panel">
            {loadingFile ? (
              <div className="text-sm text-muted-foreground flex items-center gap-2">
                <Loader2 size={14} className="animate-spin" /> Loading file…
              </div>
            ) : fileContent ? (
              <FileViewer
                file={fileContent}
                onExplain={explainFile}
                aiLoading={aiLoading}
                aiExplanation={aiExplanation}
                chatMessages={chatMessages}
                chatInput={chatInput}
                setChatInput={setChatInput}
                onSendChat={sendChatMessage}
              />
            ) : loadingReadme ? (
              <div className="text-sm text-muted-foreground flex items-center gap-2">
                <Loader2 size={14} className="animate-spin" /> Loading README…
              </div>
            ) : readme ? (
              <div>
                <div className="flex items-center gap-2 mb-3 text-sm text-muted-foreground">
                  <FileText size={14} /> README preview — select a file on the left to browse code.
                </div>
                <div className="prose prose-sm max-w-none" data-rich-html="true" data-testid="gh-readme">
                  <ReactMarkdown>{readme}</ReactMarkdown>
                </div>
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">Select a file from the tree to view its contents.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function FileViewer({
  file, onExplain, aiLoading, aiExplanation,
  chatMessages, chatInput, setChatInput, onSendChat,
}) {
  const language = detectLanguage(file.path);
  const isMarkdown = language === "markdown";
  const hasChat = chatMessages && chatMessages.length > 0;

  return (
    <div className="space-y-3" data-testid="gh-file-viewer">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-sm font-mono truncate">{file.path}</div>
        <div className="flex items-center gap-2">
          {file.size != null && (
            <span className="text-xs text-muted-foreground">{formatBytes(file.size)}</span>
          )}
          {file.html_url && (
            <a
              href={file.html_url} target="_blank" rel="noreferrer"
              className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1"
            >
              GitHub <ExternalLink size={12} />
            </a>
          )}
          <Button size="sm" onClick={onExplain} disabled={aiLoading || file.is_binary} data-testid="gh-ai-explain-btn">
            {aiLoading && !hasChat ? <Loader2 size={14} className="animate-spin mr-1" /> : <Sparkles size={14} className="mr-1" />}
            AI Explain
          </Button>
        </div>
      </div>

      {aiExplanation && !hasChat && (
        <div className="border border-purple-200 bg-purple-50/50 p-3 rounded-sm" data-testid="gh-ai-explanation">
          <div className="flex items-center gap-2 mb-2 text-xs font-medium text-purple-800">
            <Sparkles size={12} /> AI Explanation
          </div>
          <div className="prose prose-sm max-w-none" data-rich-html="true">
            <ReactMarkdown>{aiExplanation}</ReactMarkdown>
          </div>
        </div>
      )}

      {/* Chat panel — always renders below the file so admins can ask follow-ups. */}
      {!file.is_binary && (
        <ChatPanel
          messages={chatMessages}
          input={chatInput}
          setInput={setChatInput}
          onSend={onSendChat}
          loading={aiLoading}
        />
      )}

      {isMarkdown ? (
        <div className="space-y-3">
          <div className="prose prose-sm max-w-none border border-border p-4 bg-white" data-rich-html="true">
            <ReactMarkdown>{file.content || ""}</ReactMarkdown>
          </div>
          <details className="text-xs">
            <summary className="cursor-pointer text-muted-foreground">View raw markdown</summary>
            <CodeBlock code={file.content || ""} language="markdown" showLineNumbers={false} />
          </details>
        </div>
      ) : file.is_binary ? (
        <div className="text-sm text-muted-foreground italic">{file.content}</div>
      ) : (
        <CodeBlock code={file.content || ""} language={language} showLineNumbers />
      )}
    </div>
  );
}

/**
 * Small chat panel grounded in the currently opened file. Renders below the
 * header row so the admin can ask follow-up questions without losing the
 * initial AI explanation context.
 */
function ChatPanel({ messages, input, setInput, onSend, loading }) {
  const hasMessages = messages && messages.length > 0;
  return (
    <div className="border border-purple-200 bg-purple-50/40 p-3 rounded-sm" data-testid="gh-chat-panel">
      <div className="flex items-center gap-2 mb-2 text-xs font-medium text-purple-800">
        <Sparkles size={12} /> Chat with this file
      </div>
      {hasMessages && (
        <div className="space-y-3 max-h-72 overflow-auto mb-3 pr-1" data-testid="gh-chat-messages">
          {messages.map((m, idx) => (
            <div key={idx} className={m.role === "user" ? "text-right" : "text-left"}>
              <div
                className={
                  m.role === "user"
                    ? "inline-block bg-purple-600 text-white text-sm px-3 py-2 rounded-lg rounded-br-sm max-w-[85%] text-left"
                    : "inline-block bg-white border border-purple-100 text-sm px-3 py-2 rounded-lg rounded-bl-sm max-w-[85%]"
                }
              >
                {m.role === "user" ? (
                  <div className="whitespace-pre-wrap">{m.content}</div>
                ) : (
                  <div className="prose prose-sm max-w-none" data-rich-html="true">
                    <ReactMarkdown>{m.content}</ReactMarkdown>
                  </div>
                )}
              </div>
            </div>
          ))}
          {loading && (
            <div className="text-xs text-muted-foreground flex items-center gap-1">
              <Loader2 size={12} className="animate-spin" /> Thinking…
            </div>
          )}
        </div>
      )}
      <form
        className="flex items-end gap-2"
        onSubmit={(e) => { e.preventDefault(); onSend(input); }}
        data-testid="gh-chat-form"
      >
        <textarea
          rows={2}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              onSend(input);
            }
          }}
          placeholder={hasMessages ? "Ask a follow-up question…" : "Ask anything about this file (or hit Enter to start)"}
          className="flex-1 text-sm border border-border rounded-sm p-2 resize-none focus:outline-none focus:ring-1 focus:ring-primary"
          data-testid="gh-chat-input"
          disabled={loading}
        />
        <Button type="submit" size="sm" disabled={loading || !input.trim()} data-testid="gh-chat-send">
          {loading ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
        </Button>
      </form>
    </div>
  );
}

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * Prism-based highlighted code block. Kept minimal — highlights once whenever
 * `code` or `language` changes and injects the highlighted HTML.
 */
function CodeBlock({ code, language, showLineNumbers }) {
  const html = useMemo(() => {
    try {
      const lang = Prism.languages[language] ? language : "text";
      if (lang === "text") {
        return escapeHtml(code || "");
      }
      return Prism.highlight(code || "", Prism.languages[lang], lang);
    } catch (_e) {
      return escapeHtml(code || "");
    }
  }, [code, language]);

  const lineCount = (code || "").split("\n").length;
  return (
    <div
      className="max-h-[60vh] overflow-auto rounded-sm border border-neutral-800"
      style={{ background: "#2d2d2d" }}
    >
      <pre className="text-xs leading-5 m-0 p-0 flex" style={{ background: "transparent" }}>
        {showLineNumbers && (
          <code
            aria-hidden="true"
            className="select-none text-right pr-3 pl-3 py-3 border-r border-neutral-700"
            style={{ color: "#6b7280", background: "#252525", minWidth: 44 }}
          >
            {Array.from({ length: lineCount }, (_, i) => i + 1).join("\n")}
          </code>
        )}
        <code
          className={`language-${language} block px-3 py-3 flex-1`}
          style={{ background: "transparent", fontFamily: "ui-monospace, Menlo, monospace" }}
          dangerouslySetInnerHTML={{ __html: html }}
        />
      </pre>
    </div>
  );
}

function escapeHtml(str) {
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}
